/**
 * Tab "Local Sales" — datasheet bám `form_frmCtrlSalesQryLocalS.txt` (45 cột)
 * + 3 nút toolbar `frmCtrlSalesMainSubFormLocalS` (Forced close / Re-open / Filling Docs).
 *
 * Style copy `SaleContractPage > ContractListView`: filter buttons + popovers,
 * virtuoso table, total row, TablePagination defaults to "All".
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { type MouseEvent, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import { useRowSelection } from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  useGetCtrlSalesLocalSQuery,
  useReopenLocalSMutation,
  useToggleFillingDocsLocalSMutation,
  type SaleControlFinalizedFilter,
} from '../../store/api/saleControlApi'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  toggleTableSort,
  type TableSortState,
} from '../../utils/tableSort'

import SaleControlLocalSCloseDialog from './SaleControlLocalSCloseDialog'
import { formatCell, isYesText, type SaleControlColumnDef } from './saleControlAccessUi'

type FilterKind = 'search' | 'cpCode' | 'contractType' | 'certification' | 'status'

export const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

/** Cột bám thứ tự `Begin TextBox` trong `form_frmCtrlSalesQryLocalS.txt`. */
const COLUMNS: SaleControlColumnDef[] = [
  { key: 'contractSale', label: 'Contract Ref' },
  { key: 'buyersRef', label: "Buyer's Ref" },
  { key: 'contractDate', label: 'Contract Date', kind: 'date' },
  { key: 'cpCode', label: 'Client ID' },
  { key: 'clientShort', label: 'Short Name' },
  { key: 'consignee', label: 'Consignee' },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'noOfBags', label: 'Bags', kind: 'number', align: 'right' },
  { key: 'packaging', label: 'Package' },
  { key: 'quality', label: 'Quality' },
  { key: 'certification', label: 'Certification' },
  { key: 'contractType', label: 'Contract Type' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'priceUnit', label: 'Price unit' },
  { key: 'ptbfDiffs', label: 'PTBF Diffs', kind: 'number', align: 'right' },
  { key: 'futuresMonth', label: 'Futures Month' },
  { key: 'fixationCall', label: 'Fixation Call' },
  { key: 'fx', label: 'FX', kind: 'number', align: 'right' },
  { key: 'deliveryTerms', label: 'Delivery terms' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'deliveryFrom', label: 'Shipment From', kind: 'date' },
  { key: 'deliveryTo', label: 'Shipment To', kind: 'date' },
  { key: 'pssTerms', label: 'PSS terms' },
  { key: 'paymentWeightTerms', label: 'Payment weight terms' },
  { key: 'franchise', label: 'Franchise', kind: 'percent', align: 'right' },
  { key: 'paymentTerms', label: 'Payment terms' },
  { key: 'conditions', label: 'Conditions' },
  { key: 'avgPrice', label: 'Avg Price', kind: 'number', align: 'right' },
  { key: 'description', label: 'Description' },
  { key: 'finalizedText', label: 'Is Finalized' },
  { key: 'deliveredBag', label: 'Delivered Bag', kind: 'number', align: 'right' },
  { key: 'pendingBag', label: 'Pending Bag', kind: 'number', align: 'right' },
  { key: 'delivered', label: 'Delivered', kind: 'number', align: 'right' },
  { key: 'pending', label: 'Pending', kind: 'number', align: 'right' },
  { key: 'tolerance', label: 'Tolerance', kind: 'number', align: 'right' },
  { key: 'dateUpdate', label: 'Date Update', kind: 'datetime' },
  { key: 'personUpdate', label: 'Person Update' },
  { key: 'finalizedDate', label: 'Finalized Date', kind: 'datetime' },
  { key: 'sampCountAccept', label: 'Samp Accept', kind: 'number', align: 'right' },
  { key: 'sampCountReject', label: 'Samp Reject', kind: 'number', align: 'right' },
  { key: 'sampCountWaiting', label: 'Samp Waiting', kind: 'number', align: 'right' },
  { key: 'deliveryFrom2', label: 'Shipment From 2', kind: 'date' },
  { key: 'deliveryTo2', label: 'Shipment To 2', kind: 'date' },
  { key: 'vatTxt', label: 'VAT' },
  { key: 'ctrlFilldocTxt', label: 'Ctrl Fill Doc' },
]

/** Cột không cộng tổng (price/FX/rate-like — đồng bộ rule SaleContract §5.2). */
const NON_SUM_KEYS = new Set<string>([
  'price',
  'ptbfDiffs',
  'fx',
  'franchise',
  'avgPrice',
  'tolerance',
])

type GridRow = Record<string, unknown> & {
  rowKey: string
  contractSale: string
}

const distinctValues = (rows: GridRow[], key: string): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = row[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const sumNumericColumn = (rows: GridRow[], key: string): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const rowMatchesSearch = (row: GridRow, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return COLUMNS.some((col) => {
    const value = row[col.key]
    if (value == null) return false
    return String(value).toLowerCase().includes(needle)
  })
}

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return ''
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export interface SaleControlLocalSalesViewProps {
  startDate: string
  endDate: string
  isFinalized: SaleControlFinalizedFilter
  invalidRange: boolean
}

export default function SaleControlLocalSalesView({
  startDate,
  endDate,
  isFinalized,
  invalidRange,
}: SaleControlLocalSalesViewProps) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  // ----- Header filter từ SaleControlPage truyền xuống — gửi thẳng vào RTK Query
  const queryArgs = useMemo(
    () => ({
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(invalidRange ? {} : { startDate, endDate }),
      isFinalized,
    }),
    [startDate, endDate, isFinalized, invalidRange],
  )
  const { data, isLoading, isFetching, error, refetch } = useGetCtrlSalesLocalSQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  })

  const baseRows = useMemo<GridRow[]>(() => {
    const items = (data?.items ?? []) as Record<string, unknown>[]
    return items.map((rec, idx) => {
      const contractSale = String(rec.contractSale ?? '')
      const localSId = rec.localSId == null ? '' : String(rec.localSId)
      return {
        ...rec,
        rowKey: `${localSId || idx}-${contractSale}`,
        contractSale,
      }
    })
  }, [data?.items])

  const { getWidth, startResize } = useColumnWidths('sale-control.local-sales')

  // ----- Filter trên grid (sub-filter, không gọi lại API)
  const [searchTerm, setSearchTerm] = useState('')
  const [cpCodeFilter, setCpCodeFilter] = useState('')
  const [contractTypeFilter, setContractTypeFilter] = useState('')
  const [certificationFilter, setCertificationFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const {
    selectedId: selectedKey,
    toggleSelect: toggleSelectKey,
  } = useRowSelection<string>()

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const [selectOpen, setSelectOpen] = useState(false)
  const closeFilterPopover = () => {
    setFilterPopover(null)
    setSelectOpen(false)
  }
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => {
    setFilterPopover({ kind, anchor: e.currentTarget })
    if (kind !== 'search') setSelectOpen(true)
  }

  const hasActiveFilters = Boolean(
    searchTerm.trim() || cpCodeFilter.trim() || contractTypeFilter || certificationFilter || statusFilter,
  )
  const clearAllFilters = () => {
    setSearchTerm('')
    setCpCodeFilter('')
    setContractTypeFilter('')
    setCertificationFilter('')
    setStatusFilter('')
    setPage(0)
    closeFilterPopover()
  }

  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm)) return false
        if (cpCodeFilter.trim() && row.cpCode !== cpCodeFilter.trim()) return false
        if (contractTypeFilter && row.contractType !== contractTypeFilter) return false
        if (certificationFilter && row.certification !== certificationFilter) return false
        if (statusFilter && row.finalizedText !== statusFilter) return false
        return true
      }),
    [baseRows, certificationFilter, contractTypeFilter, cpCodeFilter, searchTerm, statusFilter],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, COLUMNS),
    [filteredRows, sortState],
  )

  const rows = useMemo<GridRow[]>(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        COLUMNS
          .filter((col) => col.kind === 'number' && !NON_SUM_KEYS.has(col.key))
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows],
  )

  const cpCodeOptions = useMemo(() => distinctValues(baseRows, 'cpCode'), [baseRows])
  const contractTypeOptions = useMemo(() => distinctValues(baseRows, 'contractType'), [baseRows])
  const certificationOptions = useMemo(() => distinctValues(baseRows, 'certification'), [baseRows])
  const statusOptions = useMemo(() => distinctValues(baseRows, 'finalizedText'), [baseRows])

  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'ID: —'
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const r of rows) {
      const id = Number(r.localSId)
      if (!Number.isFinite(id)) continue
      if (id < min) min = id
      if (id > max) max = id
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'ID: —'
    return `ID: ${min}–${max}`
  }, [rows])

  const minTableWidth = Math.max(960, COLUMNS.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  const toggleSort = (key: string) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<GridRow | null>(
    () => rows.find((r) => r.rowKey === selectedKey) ?? null,
    [rows, selectedKey],
  )

  // ----- Toolbar actions ----------------------------------------------------------
  const [closeDialogOpen, setCloseDialogOpen] = useState(false)
  const [confirm, setConfirm] = useState<
    | { kind: 'reopen'; contractSale: string }
    | { kind: 'fillingDocs'; contractSale: string; turnOn: boolean }
    | null
  >(null)
  const [reopen, { isLoading: reopenLoading }] = useReopenLocalSMutation()
  const [toggleFillingDocs, { isLoading: fillingLoading }] = useToggleFillingDocsLocalSMutation()

  const handleForcedClose = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if (isYesText(selectedRow.finalizedText)) {
      snack.warning('This contract is forced close!')
      return
    }
    setCloseDialogOpen(true)
  }

  const handleReopen = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if (!isYesText(selectedRow.finalizedText)) {
      snack.warning('This contract is not forced close!')
      return
    }
    setConfirm({ kind: 'reopen', contractSale: selectedRow.contractSale })
  }

  const handleFillingDocs = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setConfirm({
      kind: 'fillingDocs',
      contractSale: selectedRow.contractSale,
      turnOn: !isYesText(selectedRow.ctrlFilldocTxt),
    })
  }

  const handleConfirm = async () => {
    if (!confirm) return
    try {
      if (confirm.kind === 'reopen') {
        await reopen({ contractSale: confirm.contractSale }).unwrap()
        snack.success('Successfully Unforced')
      } else {
        await toggleFillingDocs({
          contractSale: confirm.contractSale,
          isCtrlFilldoc: confirm.turnOn,
        }).unwrap()
        snack.success('Successfully Updated')
      }
    } catch (err: unknown) {
      snack.error(getErrorMessage(err, 'Action failed'))
    } finally {
      setConfirm(null)
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar (style copy ContractListView) */}
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('search', e)}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
          <Button
            size="small"
            variant={cpCodeFilter.trim() ? 'contained' : 'outlined'}
            color={cpCodeFilter.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('cpCode', e)}
            sx={filterBarBtnSx}
          >
            Client
          </Button>
          <Button
            size="small"
            variant={contractTypeFilter ? 'contained' : 'outlined'}
            color={contractTypeFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('contractType', e)}
            sx={filterBarBtnSx}
          >
            Contract Type
          </Button>
          <Button
            size="small"
            variant={certificationFilter ? 'contained' : 'outlined'}
            color={certificationFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('certification', e)}
            sx={filterBarBtnSx}
          >
            Certification
          </Button>
          <Button
            size="small"
            variant={statusFilter ? 'contained' : 'outlined'}
            color={statusFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('status', e)}
            sx={filterBarBtnSx}
          >
            Status
          </Button>
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Tooltip
            title={
              !canEdit
                ? 'Edit permission required for Sale Control.'
                : !selectedRow
                  ? 'Select a row first.'
                  : isYesText(selectedRow.finalizedText)
                    ? 'Contract is already forced close.'
                    : 'Forced close this contract.'
            }
          >
            <span>
              <Button
                variant="contained"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !selectedRow || isYesText(selectedRow?.finalizedText)}
                onClick={handleForcedClose}
              >
                Forced close
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              !canEdit
                ? 'Admin only.'
                : !selectedRow
                  ? 'Select a row first.'
                  : !isYesText(selectedRow.finalizedText)
                    ? 'Contract is not forced close.'
                    : 'Re-open a forced-closed contract (admin only).'
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !selectedRow || !isYesText(selectedRow?.finalizedText)}
                onClick={handleReopen}
              >
                Re-open
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              !selectedRow
                ? 'Select a row first.'
                : isYesText(selectedRow.ctrlFilldocTxt)
                  ? 'Uncheck completed documents (admin / allowlist).'
                  : 'Check completed documents (admin / allowlist).'
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                sx={actionToolbarBtnSx}
                disabled={!selectedRow}
                onClick={handleFillingDocs}
              >
                Filling Docs
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getErrorMessage(error, 'Failed to load Sale Control rows.')}
        </Alert>
      ) : null}

      <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Loaded: {baseRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.68rem' },
                '& .MuiTablePagination-select': { fontSize: '0.68rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' },
              }}
            />
            <Button size="small" variant="text" onClick={() => refetch()} sx={filterBarBtnSx}>
              Refresh
            </Button>
          </Stack>
        </Box>

        <Popover
          open={filterPopover !== null}
          anchorEl={filterPopover?.anchor ?? null}
          onClose={closeFilterPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 320 } }}
        >
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                label="Search rows"
                placeholder="Contract, client, …"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPage(0)
                }}
              />
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSearchTerm('')
                  setPage(0)
                  closeFilterPopover()
                }}
              >
                Clear Search
              </Button>
            </Stack>
          )}
          {filterPopover?.kind === 'cpCode' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Client</InputLabel>
              <Select
                label="Client"
                value={cpCodeFilter}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => {
                  setSelectOpen(false)
                  closeFilterPopover()
                }}
                onChange={(e) => {
                  setCpCodeFilter(String(e.target.value))
                  setPage(0)
                }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {cpCodeOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'contractType' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Contract Type</InputLabel>
              <Select
                label="Contract Type"
                value={contractTypeFilter}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => {
                  setSelectOpen(false)
                  closeFilterPopover()
                }}
                onChange={(e) => {
                  setContractTypeFilter(String(e.target.value))
                  setPage(0)
                }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {contractTypeOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'certification' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Certification</InputLabel>
              <Select
                label="Certification"
                value={certificationFilter}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => {
                  setSelectOpen(false)
                  closeFilterPopover()
                }}
                onChange={(e) => {
                  setCertificationFilter(String(e.target.value))
                  setPage(0)
                }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {certificationOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'status' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => {
                  setSelectOpen(false)
                  closeFilterPopover()
                }}
                onChange={(e) => {
                  setStatusFilter(String(e.target.value))
                  setPage(0)
                }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {statusOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Sale Control rows"
              description="Refresh the page or contact admin if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No rows found"
              description="Try changing the Shipment Period / Is Finalized filter at the top, or the grid filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {COLUMNS.map((col) => {
                    const w = getWidth(col.key)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align ?? (col.kind === 'number' ? 'right' : 'left')}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', fontWeight: 700, position: 'relative' }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {COLUMNS.map((col, index) => {
                    const total = totalRowValues.get(col.key)
                    const text = total == null ? '' : formatNumber(total)
                    const w = getWidth(col.key)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align ?? (col.kind === 'number' ? 'right' : 'left')}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: GridRow) =>
                COLUMNS.map((col) => {
                  const val = formatCell(row, col)
                  const w = getWidth(col.key)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? (col.kind === 'number' ? 'right' : 'left')}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                /* eslint-disable @typescript-eslint/no-explicit-any -- react-virtuoso typing buộc dùng `any` cho TableRow data-item-index hook (đồng bộ pattern `ContractListView.tsx`). */
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  const rowKey = row?.rowKey ?? null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowKey != null && rowKey === selectedKey}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowKey != null) toggleSelectKey(rowKey)
                      }}
                    />
                  )
                },
                /* eslint-enable @typescript-eslint/no-explicit-any */
              }}
            />
          )}
        </Box>
      </Box>

      <SaleControlLocalSCloseDialog
        open={closeDialogOpen}
        contractSale={selectedRow?.contractSale ?? ''}
        pending={(selectedRow?.pending as number | string | null | undefined) ?? null}
        onClose={() => setCloseDialogOpen(false)}
      />

      <ConfirmDialog
        open={confirm?.kind === 'reopen'}
        title="Unforced close"
        message="Do you want to unforce close this contract?"
        confirmLabel="Re-open"
        cancelLabel="Cancel"
        loading={reopenLoading}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />

      <ConfirmDialog
        open={confirm?.kind === 'fillingDocs'}
        title="Filling Docs"
        message={
          confirm?.kind === 'fillingDocs' && confirm.turnOn
            ? 'Do you want to check the completed documents?'
            : 'Do you want to uncheck the completed documents?'
        }
        confirmLabel="OK"
        cancelLabel="Cancel"
        loading={fillingLoading}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </Box>
  )
}
